// What the Recipes library shows (redesign slice 7a; wireframes 8 and 17;
// docs/2026-10-04-recipes-design.md): the filters, sort, whose each recipe
// is, its status line, and "Recently shared with you". Pure functions,
// tested in tests/recipe-library.test.mjs.
import { DAYS, DIET_TAG_LABELS, MEAL_SLOTS } from "./constants";
import { normalizeDishes } from "./planUtils";
import { mealDate } from "./planScaling";
import { seasonOf } from "./pickerView";
import { formatMinutes } from "./utils";
import type { Day, PlansByWeek, Recipe } from "./types";

export type Whose = "mine" | "household" | "shared" | "public";
export type TimeLimit = "any" | "30" | "60";
export type SortBy = "newest" | "name" | "quickest";

export interface LibraryFilters {
  query: string;
  favorites: boolean;
  whose: Whose | "all";
  dishType: string; // "" = any
  meal: string;
  season: string;
  inSeason: boolean; // the chip: the current season
  diet: string;
  method: string;
  cuisine: string;
  time: TimeLimit;
}

export const NO_LIBRARY_FILTERS: LibraryFilters = { query: "", favorites: false, whose: "all", dishType: "", meal: "", season: "", inSeason: false, diet: "", method: "", cuisine: "", time: "any" };

// How many filters the "All filters · n" button counts (search excluded).
export function activeFilterCount(f: LibraryFilters): number {
  return [f.favorites, f.whose !== "all", !!f.dishType, !!f.meal, !!f.season || f.inSeason, !!f.diet, !!f.method, !!f.cuisine, f.time !== "any"].filter(Boolean).length;
}

export interface Viewer {
  userId: string;
  sharedWithMe: Map<string, string>; // recipe id -> when shared (ISO)
  isFavorite: (r: Recipe) => boolean;
}

// Whose a recipe is, from the viewer's side: theirs, shared with them
// directly, public, or (visible any other way) a household member's.
export function whoseRecipe(r: Recipe, viewer: Viewer): Whose {
  if (r.ownerUserId === viewer.userId) return "mine";
  if (viewer.sharedWithMe.has(r.id)) return "shared";
  if (r.isPublic) return "public";
  return "household";
}

export const totalMinutes = (r: Pick<Recipe, "activeTime" | "cookTime">) => (r.activeTime || 0) + (r.cookTime || 0);

export function filterLibrary(recipes: Recipe[], f: LibraryFilters, viewer: Viewer, now: Date): Recipe[] {
  const q = f.query.trim().toLowerCase();
  const season = f.inSeason ? seasonOf(now) : f.season;
  return recipes.filter((r) => {
    if (q && !(r.title || "").toLowerCase().includes(q) && !(r.source || "").toLowerCase().includes(q)) return false;
    if (f.favorites && !viewer.isFavorite(r)) return false;
    if (f.whose !== "all" && whoseRecipe(r, viewer) !== f.whose) return false;
    if (f.dishType && r.recipeType !== f.dishType) return false;
    if (f.meal && !(r.mealTypes || []).includes(f.meal)) return false;
    if (season && !(r.seasons || []).includes(season)) return false;
    if (f.diet && !(r.dietTags || []).includes(f.diet)) return false;
    if (f.method && !(r.cookingMethod || []).includes(f.method)) return false;
    if (f.cuisine && (r.cuisine || "").trim() !== f.cuisine) return false;
    if (f.time !== "any") {
      const t = totalMinutes(r);
      if (t === 0 || t > Number(f.time)) return false;
    }
    return true;
  });
}

const byTitle = (a: Recipe, b: Recipe) => (a.title || "").localeCompare(b.title || "", undefined, { sensitivity: "base", numeric: true });

export function sortLibrary(recipes: Recipe[], by: SortBy): Recipe[] {
  const out = [...recipes];
  if (by === "name") return out.sort(byTitle);
  if (by === "quickest") return out.sort((a, b) => (totalMinutes(a) || Infinity) - (totalMinutes(b) || Infinity) || byTitle(a, b));
  return out.sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || "") || byTitle(a, b));
}

// "45 min · serves 4 · vegetarian"
export function libraryMeta(r: Recipe): string {
  const t = totalMinutes(r);
  const diet = (r.dietTags || []).map((d) => (DIET_TAG_LABELS as Record<string, string>)[d]?.toLowerCase()).filter(Boolean)[0];
  return [t ? formatMinutes(t) : "", r.servings ? `serves ${r.servings}` : "", diet || ""].filter(Boolean).join(" · ");
}

// Recipe id -> when it is next planned, from today on: "tonight",
// "today", "tomorrow", "Sunday" (within a week), else "Oct 14".
export function nextPlanned(plansByWeek: PlansByWeek, now: Date): Map<string, string> {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const best = new Map<string, { date: Date; slot: number }>();
  for (const [weekStart, week] of Object.entries(plansByWeek)) {
    for (const day of DAYS) {
      const date = mealDate(weekStart, day as Day);
      if (date < today) continue;
      for (const [si, slot] of MEAL_SLOTS.entries()) {
        for (const id of Object.values(normalizeDishes(week.days?.[day as Day]?.[slot]) as Record<string, string>)) {
          if (!id) continue;
          const was = best.get(id);
          if (!was || date < was.date || (date.getTime() === was.date.getTime() && si < was.slot)) best.set(id, { date, slot: si });
        }
      }
    }
  }
  const out = new Map<string, string>();
  for (const [id, { date, slot }] of best) {
    const days = Math.round((date.getTime() - today.getTime()) / 86400000);
    const label =
      days === 0 ? (MEAL_SLOTS[slot] === "dinner" ? "tonight" : "today") : days === 1 ? "tomorrow" : days < 7 ? date.toLocaleDateString(undefined, { weekday: "long" }) : date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    out.set(id, label);
  }
  return out;
}

// The line under a recipe's title, most useful first.
export function libraryStatus(r: Recipe, viewer: Viewer, planned: Map<string, string>, sharedByMe: Set<string>): string {
  if (planned.has(r.id)) return `In your plan: ${planned.get(r.id)}`;
  const whose = whoseRecipe(r, viewer);
  if (whose === "shared") return "Shared with you";
  if (whose === "household") return "From a household member";
  if (whose === "public") return "Public";
  if (r.isPublic) return "Public";
  if (sharedByMe.has(r.id)) return "Shared by you";
  return "";
}

// Shared with me in the last 30 days, newest share first, at most `limit`.
export function recentlyShared(recipes: Recipe[], viewer: Viewer, now: Date, limit = 3): Recipe[] {
  const since = new Date(now.getTime() - 30 * 86400000).toISOString();
  return recipes
    .filter((r) => (viewer.sharedWithMe.get(r.id) ?? "") >= since)
    .sort((a, b) => viewer.sharedWithMe.get(b.id)!.localeCompare(viewer.sharedWithMe.get(a.id)!))
    .slice(0, limit);
}

// Cuisines in use, for the filter list.
export function cuisinesIn(recipes: Recipe[]): string[] {
  return [...new Set(recipes.map((r) => (r.cuisine || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
}
